import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  model,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { catchError, EMPTY, tap } from 'rxjs';
import * as fromGenerated from '../../../_generated';
import { AnalyticsService } from '../../../analytics/analytics.service';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromLayout from '../../../layout';
import * as fromShared from '../../shared';
import { ChatWithMessagesActions } from '../../store/chats.actions';
import { chatsFeature, ChatsState } from '../../store/chats.reducer';
import {
  DocumentViewerContentComponent,
  DocumentViewerZoomSetting,
} from './document-viewer-content.component';
import {
  DocumentViewerHeaderComponent,
  DocumentViewerZoomFactorClickedEvent,
  DocumentViewerZoomFactorLimitReached,
} from './document-viewer-header.component';
import {
  ZOOM_FACTOR_LIMIT,
  ZOOM_SETTING_DEFAULT,
} from './document-viewer.constants';
import {
  calculateNewZoomSetting,
  removeWordWrappingHyphens,
  zoomFactorToZoomSetting,
} from './document-viewer.helper';
import { SourceNavigatorComponent } from './source-navigator.component';

@Component({
  selector: 'squadbox-document-viewer-smart',
  imports: [
    DocumentViewerHeaderComponent,
    DocumentViewerContentComponent,
    SourceNavigatorComponent,
    fromFeatureFlags.FeatureFlagsDirective,
    NgClass,
  ],
  template: `
    <div
      class="hide-scrollbar w-full h-screen overflow-y-none opacity-100 flex flex-col"
      [ngClass]="{
        'border-r border-neutral-200':
          viewMode() !== 'doc-viewer-assistant-profile',
      }"
    >
      @if (document(); as document) {
        <squadbox-document-viewer-header
          [filename]="document.filename"
          [fileType]="getFileTypeIconFromExtension(document.extension)"
          [zoomFactorLimitReached]="zoomFactorLimitReached()"
          [isMobile]="isMobile()"
          (zoomClicked)="handleDocumentViewerHeaderZoomClicked($event)"
          (downloadClicked)="handleDocumentViewerHeaderDownloadClicked()"
          (closeClicked)="handleDocumentViewerHeaderCloseClicked()"
        ></squadbox-document-viewer-header>
        <squadbox-document-viewer-content
          [src]="document.presignedUrl.url"
          [relevantPages]="relevantPages()"
          [relevantTexts]="relevantTexts()"
          [(zoomSetting)]="zoomSetting"
          [(zoomFactor)]="zoomFactor"
          [zoomFactorLimits]="zoomFactorLimits()"
          [isMobile]="isMobile()"
          [isCitationsLoading]="isCitationsLoading()"
          [(page)]="currentPage"
          [(pagesWithVisiblePins)]="pagesWithVisiblePins"
          [(isLoading)]="isLoading"
        ></squadbox-document-viewer-content>
        @if (!isLoading() && pagesWithVisiblePins().length > 0) {
          <div
            class="absolute right-0 bottom-4 z-50 flex justify-center"
            [class]="isMobile() ? 'left-0' : 'left-40'"
            [squadboxFeatureFlag]="'ff-navigate-between-highlights'"
          >
            <squadbox-source-navigator
              [currentSourceIndex]="currentSourceIndex()"
              [totalSources]="totalSources()"
              [currentPage]="currentPage()"
              [firstPageWithPin]="pagesWithVisiblePins()[0]"
              (updatedCurrentIndex)="navigateToPageFromUpdatedIndex($event)"
            ></squadbox-source-navigator>
          </div>
        }
      }
    </div>
  `,
  styles: [``],
})
export class DocumentViewerSmartComponent {
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);
  private readonly documentsService = inject(fromGenerated.DocumentsService);
  private readonly analyticsService = inject(AnalyticsService);

  public readonly zoomSetting =
    model<DocumentViewerZoomSetting>(ZOOM_SETTING_DEFAULT);
  public readonly zoomFactor = model<number>();
  public readonly zoomFactorLimits = signal<{
    min: number;
    max: number;
  }>(ZOOM_FACTOR_LIMIT);
  public readonly zoomFactorLimitReached =
    // eslint-disable-next-line sonarjs/function-return-type
    computed<DocumentViewerZoomFactorLimitReached>(() => {
      const zoomFactor = this.zoomFactor();
      const zoomFactorLimits = this.zoomFactorLimits();

      if (zoomFactor) {
        if (zoomFactor >= zoomFactorLimits.max) {
          return 'in';
        } else if (zoomFactor <= zoomFactorLimits.min) {
          return 'out';
        }
      }

      return false;
    });

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly viewMode = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectViewMode,
  );
  private readonly ffDocumentViewerHighlightText =
    this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
        'ff-document-viewer-highlight-text',
      ),
    );
  private readonly selectedChatMessageId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatMessageId,
  );
  private readonly selectedDocumentViewModel = this.chatsStore.selectSignal(
    fromShared.selectSelectedDocumentViewModel,
  );

  public readonly document = signal<{
    chatMessageId: number | null;
    documentUuid: string;
    filename: string;
    extension: string;
    presignedUrl: {
      url: string;
      expiresAt: string;
    };
  }>({
    chatMessageId: null,
    documentUuid: '',
    filename: '',
    extension: '',
    presignedUrl: { url: '', expiresAt: '' },
  });
  public readonly relevantPages = computed(() => {
    return (
      this.selectedDocumentViewModel()?.documentChunks.reduce(
        (acc, chunk) => [...acc, ...chunk.pageNumbers],
        [] as number[],
      ) ?? []
    );
  });
  public readonly relevantTexts = computed(() => {
    const documentViewModel = this.selectedDocumentViewModel();

    if (
      documentViewModel?.documentCitations &&
      documentViewModel?.documentCitations.length > 0
    ) {
      return documentViewModel.documentCitations.map((citation) =>
        removeWordWrappingHyphens(citation.content),
      );
    }

    // Fallback to chunks if citations are empty for some reason and chunks aren't
    return documentViewModel?.documentChunks
      ? documentViewModel.documentChunks
          .flatMap((chunk) => chunk.content.split('\n'))
          .filter((line) => line.trim() !== '')
          .map((line) => removeWordWrappingHyphens(line))
      : [];
  });
  public readonly isCitationsLoading = this.chatsStore.selectSignal(
    chatsFeature.selectIsCitationsLoading,
  );

  private readonly selectedDocument = computed(() => {
    return this.selectedDocumentViewModel()?.document ?? null;
  });
  private readonly hasNonEmptyDocumentChunks = computed(() => {
    const vm = this.selectedDocumentViewModel();
    return (
      vm?.documentChunks.some(
        (c) => c.content && c.content.trim().length > 0,
      ) ?? false
    );
  });
  private readonly hasCitations = computed(() => {
    const vm = this.selectedDocumentViewModel();
    return vm ? vm.documentCitations.length > 0 : false;
  });
  private readonly hasDocumentChanged = computed(() => {
    const currentDocument = this.selectedDocument();
    if (!currentDocument) {
      return false;
    }

    const previousDocument = this.document();
    const currentChatId = this.selectedChatMessageId();
    return (
      previousDocument.documentUuid !== currentDocument.uuid ||
      previousDocument.chatMessageId !== currentChatId
    );
  });
  private readonly shouldRequestCitations = computed(() => {
    return (
      this.ffDocumentViewerHighlightText() &&
      !!this.selectedChatMessageId() &&
      this.hasNonEmptyDocumentChunks() &&
      !this.hasCitations() &&
      this.hasDocumentChanged()
    );
  });

  public readonly currentSourceIndex = signal<number>(0);
  public readonly totalSources = signal<number>(1);
  public readonly pagesWithVisiblePins = model<number[]>([]);
  public readonly isLoading = model<boolean>(false);
  public readonly currentPage = model<number>(1);

  @HostListener('window:popstate', ['$event'])
  public onPopState(event: PopStateEvent) {
    event.preventDefault();
    this.handleDocumentViewerHeaderCloseClicked();
  }

  constructor() {
    // Open PDF external links in a new tab
    pdfDefaultOptions.externalLinkTarget = 2;

    // Request citations if required
    effect(() => {
      if (!this.shouldRequestCitations()) {
        return;
      }

      const selectedChatMessageId = this.selectedChatMessageId();
      const selectedDocument = this.selectedDocument();
      if (!selectedChatMessageId || !selectedDocument) {
        return;
      }

      this.chatsStore.dispatch(
        ChatWithMessagesActions.requestDocumentCitations({
          chatMessageId: selectedChatMessageId,
          documentUuid: selectedDocument.uuid,
        }),
      );
    });

    // Reset source index
    effect(() => {
      if (this.hasNonEmptyDocumentChunks()) {
        this.currentSourceIndex.set(0);
      }
    });

    // Generate or refresh presigned URL after change
    effect(() => {
      if (!this.hasDocumentChanged()) {
        return;
      }

      const selectedDocument = this.selectedDocument();
      const selectedChatMessageId = this.selectedChatMessageId();
      if (!selectedDocument) {
        return;
      }

      this.documentsService
        .documentsControllerGeneratePresignedUrlV1(selectedDocument.uuid)
        .pipe(
          tap((presigned) => {
            this.document.set({
              chatMessageId: selectedChatMessageId,
              documentUuid: selectedDocument.uuid,
              filename: selectedDocument.filename,
              extension: selectedDocument.extension,
              presignedUrl: {
                url: presigned.url,
                expiresAt: presigned.expiresAt,
              },
            });
          }),
        )
        .subscribe();

      this.analyticsService
        .sendDocumentViewerOpenedEvent({
          fileExtension: selectedDocument.extension,
          fileSize: selectedDocument.size,
        })
        .subscribe();
    });

    // Update total sources when pages with visible pins change
    effect(() => {
      this.totalSources.set(this.pagesWithVisiblePins().length);
    });

    // Update source index based on current page and visible pins
    effect(() => {
      const currentPageValue = this.currentPage();
      const pinsPages = this.pagesWithVisiblePins();
      const currentIndex = this.currentSourceIndex();

      if (pinsPages.length > 0) {
        // Find the pin index corresponding to the current page or the last pin passed
        let newIndex = 0;
        for (let i = 0; i < pinsPages.length; i++) {
          if (currentPageValue >= pinsPages[i]) {
            newIndex = i;
          } else {
            break;
          }
        }

        // Update only if index has changed
        if (currentIndex !== newIndex) {
          this.currentSourceIndex.set(newIndex);
        }
      }
    });

    this.initializeHistoryHandler();
  }

  public navigateToPageFromUpdatedIndex(updatedIndex: number) {
    const pinsPages = this.pagesWithVisiblePins();

    const pageNumber = pinsPages[updatedIndex];
    this.currentPage.set(pageNumber);
  }

  public handleDocumentViewerHeaderCloseClicked() {
    this.chatsStore.dispatch(
      fromShared.DocumentViewerActions.closeDocumentViewer(),
    );
  }

  public handleDocumentViewerHeaderZoomClicked(
    event: DocumentViewerZoomFactorClickedEvent,
  ) {
    const zoomFactor = this.zoomFactor();
    if (zoomFactor) {
      const zoomSetting = zoomFactorToZoomSetting(zoomFactor);
      this.zoomSetting.set(calculateNewZoomSetting(zoomSetting, event));
    }
  }

  public getFileTypeIconFromExtension(extension: string) {
    return fromSeriousSystem.getFileTypeIconFromExtension(extension);
  }

  public handleDocumentViewerHeaderDownloadClicked() {
    const doc = this.document();
    if (!doc?.documentUuid) {
      return;
    }

    const expiresAt = new Date(doc.presignedUrl.expiresAt);
    const now = new Date();

    // Buffer of 30 seconds
    if (expiresAt.getTime() - now.getTime() > 30_000) {
      this.triggerDownload(doc.presignedUrl.url, doc.filename);
    } else {
      this.documentsService
        .documentsControllerGeneratePresignedUrlV1(doc.documentUuid)
        .pipe(
          tap((presigned) => {
            this.document.update((d) => ({
              ...d,
              presignedUrl: {
                url: presigned.url,
                expiresAt: presigned.expiresAt,
              },
            }));
            this.triggerDownload(presigned.url, doc.filename);
          }),
          catchError((error) => {
            console.error('Error generating presigned URL for download', error);
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  private triggerDownload(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Track file download event
    this.analyticsService
      .sendFileDownloadedEvent({
        from: 'document_viewer',
      })
      .subscribe();
  }

  private initializeHistoryHandler() {
    // Add an empty entry in the history when the component is mounted to close
    // the document viewer when pressing the back button without navigating to
    // a previous page
    window.history.pushState(null, '', window.location.href);
  }
}
